import AlarmOffRoundedIcon from '@mui/icons-material/AlarmOffRounded'
import AlarmRoundedIcon from '@mui/icons-material/AlarmRounded'
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd'
import CloseIcon from '@mui/icons-material/Close'
import HolidayVillageIcon from '@mui/icons-material/HolidayVillage'
import SearchIcon from '@mui/icons-material/Search'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'

import LocalStorageButtons from '@components/LocalStorageButtons'
import { FilterAutosuggest } from '@components/toolbar/FilterAutosuggest'
import FilterFromToDate from '@components/toolbar/FilterFromToDate'
import FilterGridContainer from '@components/toolbar/FilterGridContainer'
import FilterGridItem from '@components/toolbar/FilterGridItem'
import FilterKpi from '@components/toolbar/FilterKpi'
import { FilterMyList } from '@components/toolbar/FilterMyList'
import { FilterSelectGebied } from '@components/toolbar/FilterSelectGebied'
import { FilterSelectObject } from '@components/toolbar/FilterSelectObject'
import { FilterSelectString } from '@components/toolbar/FilterSelectString'
import { FilterToggleButton } from '@components/toolbar/FilterToggleButton'
import type { FilterOption, GroupedFilterOption } from '@models/Filter'
import type { Gebied } from '@models/Gebied'

import { useLocalFilters } from '../../../core/hooks/useLocalFilters'
import { StyledIconButton } from '../../../theme/components/StyledIconButton'
import { queryKey } from '../hooks/useProjectenSearch'
import type { ProjectenSearchFilters as Filters } from '../types/ProjectenSearch'

const kpiTooltips = {
  red: 'Laat alleen de verlopen KPIs zien',
  amber: 'Laat bijna verlopen KPIs zien',
  green: 'Laat alle nog niet verlopen KPIs zien',
  grey: 'Laat alle projecten zien',
}

const acties = [
  { code: 'quickscan', label: 'Quickscan' },
  { code: 'verstuurOpdrachten', label: 'Verstuur opdrachten' },
  { code: 'planningConflict', label: 'Planning conflict werkvoorbereiding' },
  {
    code: 'planningConflictHoofdleiding',
    label: 'Planning conflict Hoofdleiding',
  },
  {
    code: 'planningConflictAansluiting',
    label: 'Planning conflict Aansluiting',
  },
  { code: 'opleverenBijlagen', label: 'Opleveren bijlagen' },
  {
    code: 'gesplitsteWerkvoorbereiding',
    label: 'Gesplitste werkvoorbereiding',
  },
]

const planningsverschilOptions: FilterOption[] = [
  { label: 'Wijziging', code: 'wijziging' },
  { label: 'Afwijking', code: 'afwijking' },
]

interface Props {
  onChange: (filters: Filters, removeSort?: boolean) => void
  filters: Filters
  initialFilters: Filters
  onSearchChange: (search: any) => void
  options: any
  onToggleShowActions: () => void
  showActions: boolean
  showPlanningsverschil: boolean
  onReset: () => void
  onRefresh: () => void
  filterOptions: {
    gebieden: Gebied[]
    netbeheerders: string[]
    uitvoerendeAannemers: string[]
    actiehouders: GroupedFilterOption[]
    processtappen: string[]
    labels: FilterOption[]
  }
}

export const ProjectenFilters = ({
  onChange,
  filters,
  initialFilters,
  onSearchChange,
  options,
  onToggleShowActions,
  showActions,
  showPlanningsverschil,
  onRefresh,
  onReset,
  filterOptions: {
    gebieden,
    netbeheerders,
    uitvoerendeAannemers,
    actiehouders,
    processtappen,
    labels: labelOptions,
  },
}: Props) => {
  const { localFilters, handleFilterChange, handleZoeken, handleReset } =
    useLocalFilters<Filters>({
      filters,
      initialFilters,
      onChange,
      onRefresh,
      onReset,
    })

  const {
    fromDate,
    toDate,
    fromGeplandeUitvoeringsdatum,
    toGeplandeUitvoeringsdatum,
    search,
    gebied,
    processtap,
    active,
    kpi,
    netbeheerder,
    actiehouder,
    actie,
    uitvoerende,
    labels,
    myList,
    planningsverschil,
  } = localFilters

  return (
    <FilterGridContainer>
      <FilterGridItem>
        <FilterSelectGebied
          options={gebieden}
          value={gebied}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectString
          multiple
          options={processtappen}
          label="Processtap"
          value={processtap}
          filterKey="processtap"
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectString
          options={netbeheerders}
          label="Netbeheerder"
          value={netbeheerder}
          filterKey="netbeheerder"
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectString
          options={uitvoerendeAannemers}
          label="Uitvoerende Aannemer"
          value={uitvoerende}
          filterKey="uitvoerende"
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectObject
          options={actiehouders}
          label="Actiehouder"
          filterKey="actiehouder"
          value={actiehouder}
          onFilterChange={handleFilterChange}
          groupByProp="group"
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectObject
          options={acties}
          label="Actie"
          filterKey="actie"
          value={actie}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      {showPlanningsverschil && (
        <FilterGridItem>
          <FilterSelectObject
            multiple
            options={planningsverschilOptions}
            label="Planningsverschil"
            value={planningsverschil}
            filterKey="planningsverschil"
            onFilterChange={handleFilterChange}
          />
        </FilterGridItem>
      )}

      <FilterGridItem minColumns="double">
        <FilterFromToDate
          fromDate={fromDate}
          toDate={toDate}
          onFilterChange={handleFilterChange}
          label="Aangevraagd"
        />
      </FilterGridItem>

      <FilterGridItem minColumns="double">
        <FilterFromToDate
          fromDate={fromGeplandeUitvoeringsdatum}
          toDate={toGeplandeUitvoeringsdatum}
          onFilterChange={handleFilterChange}
          label="Gepland"
          prop="GeplandeUitvoeringsdatum"
        />
      </FilterGridItem>

      {labelOptions.length > 0 && (
        <FilterGridItem>
          <FilterSelectObject
            multiple
            options={labelOptions}
            label="Labels"
            filterKey="labels"
            value={labels}
            onFilterChange={handleFilterChange}
          />
        </FilterGridItem>
      )}

      <FilterGridItem>
        <FilterAutosuggest
          search={search}
          options={options}
          onFilterChange={handleFilterChange}
          onSearchChange={onSearchChange}
        />
      </FilterGridItem>

      <FilterGridItem
        minColumns="full"
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <FilterKpi
          onFilterChange={handleFilterChange}
          kpi={kpi}
          tooltips={kpiTooltips}
        />

        <StyledIconButton
          icon={<CloseIcon />}
          title="Reset filters"
          onClick={handleReset}
        />
        <StyledIconButton
          icon={<SearchIcon />}
          title="Filters toepassen"
          onClick={handleZoeken}
        />

        <FilterMyList myList={myList} onFilterChange={handleFilterChange} />

        <FilterToggleButton
          filterKey="active"
          value={active}
          onFilterChange={handleFilterChange}
          activeIcon={<AlarmRoundedIcon />}
          inactiveIcon={<AlarmOffRoundedIcon />}
          activeTitle="Klik om ook inactieve projecten te tonen"
          inactiveTitle="Klik om alleen actieve projecten te tonen"
        />

        <Tooltip
          title={
            showActions
              ? 'Klik om locatie informatie te tonen'
              : 'Klik om acties te tonen'
          }
        >
          <IconButton data-testid="actiebutton" onClick={onToggleShowActions}>
            {showActions ? <HolidayVillageIcon /> : <AssignmentIndIcon />}
          </IconButton>
        </Tooltip>

        <LocalStorageButtons
          name={queryKey}
          storable={localFilters}
          onClickLoad={handleFilterChange}
        />
      </FilterGridItem>
    </FilterGridContainer>
  )
}
